import {compact} from 'underscore';

import {assert} from 'Asserts';
import {
  ProcessSchemaMapperType,
  ProcessMapperTokenType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupConfigSpec, LookupStepSpec, LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {
  ProcessFlowchartSchemaFieldMapperConfigSpec,
  ProcessFlowchartSchemaMapperConfigSpec,
  ProcessFlowchartSchemaMapperSpec,
} from 'processes/dao/ProcessFlowchartConfigDAO';
import {
  findAllUpstreamMappableNodeIds,
  isMappableTargetNode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {
  ProcessFlowchartEdgeType,
  TransitionSource,
  FlowchartStaticEdgeData,
  CustomMappingData,
  TaskSchemaNodeSectionType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

import {getFieldInfoFromDataFlowEdgeHandle} from './flowchartIdentifierUtils';
import * as GetClientId from './getClientId';
import {
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
  TaskSchemaStaticNodeData,
} from './processFlowchartConfigEditorNodeTypes';
import {getDefaultTransitionSource, getTransitionSourceByNodeIdPairKey} from './TransitionSourceUtils';

interface BuildSchemaMapperConfigsFromStaticDataRunnerArgs {
  nodes: Array<StaticFlowchartNodeData>;
  edges: Array<FlowchartStaticEdgeData>;
  customMappingDataByEdgeId: Record<string, CustomMappingData>;
  transitionSourceByNodeIdPair: Record<string, TransitionSource>;
}

interface BuildSchemaMapperConfigsFromStaticDataRunnerResult {
  schemaMapperConfigSpecs: Array<ProcessFlowchartSchemaMapperConfigSpec>;
  inboundSchemaMapperConfigsByNodeId: Record<string, Array<ProcessFlowchartSchemaMapperSpec>>;
}

interface ProcessFlowchartSchemaFieldMapperConfig {
  fieldMapperConfig: ProcessFlowchartSchemaFieldMapperConfigSpec;
  fromTaskDefinitionVersionId: string;
  fromNodeConfigClientId: string;
}

class BuildSchemaMapperConfigsFromStaticDataRunner {
  private readonly nodes: Array<StaticFlowchartNodeData>;
  private readonly edges: Array<FlowchartStaticEdgeData>;
  private readonly customMappingDataByEdgeId: Record<string, CustomMappingData>;
  private readonly transitionSourceByNodeIdPair: Record<string, TransitionSource>;

  private inboundSchemaMapperConfigsByNodeId: Record<string, Array<ProcessFlowchartSchemaMapperSpec>>;
  private rootNode: TaskSchemaStaticNodeData | undefined;
  // Mapping of a hashkey based on PTDVs to schema mapper configs for deduplication
  private existingSchemaMapperConfigs: Map<string, ProcessFlowchartSchemaMapperConfigSpec>;

  constructor(args: BuildSchemaMapperConfigsFromStaticDataRunnerArgs) {
    this.nodes = args.nodes;
    this.edges = args.edges;
    this.customMappingDataByEdgeId = args.customMappingDataByEdgeId;
    this.transitionSourceByNodeIdPair = args.transitionSourceByNodeIdPair;

    this.inboundSchemaMapperConfigsByNodeId = {};
    this.rootNode = this.nodes.find(
      (node): node is TaskSchemaStaticNodeData =>
        isStaticTaskSchemaNodeData(node) &&
        node.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
    );
    this.existingSchemaMapperConfigs = new Map();
  }

  /**
   * Creates a unique key for schema mapper config deduplication based on task definition versions.
   */
  private getSchemaMapperConfigKey(
    fromTaskDefinitionVersionId: string,
    toTaskDefinitionVersionId: string,
    mapsFromType: ProcessMapperTokenType,
    mapsToType: ProcessMapperTokenType
  ): string {
    return `${fromTaskDefinitionVersionId}-${toTaskDefinitionVersionId}-${mapsFromType}-${mapsToType}`;
  }

  run(): BuildSchemaMapperConfigsFromStaticDataRunnerResult {
    const schemaMapperConfigSpecs: Array<ProcessFlowchartSchemaMapperConfigSpec> = [];
    for (const node of this.nodes) {
      if (isMappableTargetNode({id: node.id, data: node})) {
        assert(isStaticTaskSchemaNodeData(node), 'Only task schema type nodes are expected to be mapped to');
        schemaMapperConfigSpecs.push(...this.buildSchemaMapperConfigsForTargetNode(node));
      }
    }

    return {
      schemaMapperConfigSpecs,
      inboundSchemaMapperConfigsByNodeId: this.inboundSchemaMapperConfigsByNodeId,
    };
  }

  /**
   * This currently assumes all transitions are non-merging, and will create an inbound schema mapper config
   * for each control flow edge going to a target node.
   */
  private buildSchemaMapperConfigsForTargetNode(
    targetNode: TaskSchemaStaticNodeData
  ): Array<ProcessFlowchartSchemaMapperConfigSpec> {
    const upstreamMappableNodeIds = findAllUpstreamMappableNodeIds(
      {nodes: this.nodes.map((node) => ({id: node.id, data: node})), edges: this.edges},
      targetNode.id
    );
    if (this.inboundSchemaMapperConfigsByNodeId[targetNode.id] == null) {
      this.inboundSchemaMapperConfigsByNodeId[targetNode.id] = [];
    }

    const upstreamMappableNodes = this.nodes.filter((node) => upstreamMappableNodeIds.includes(node.id));
    return compact(
      upstreamMappableNodes.map((sourceNode) => {
        assert(
          isStaticTaskSchemaNodeData(sourceNode),
          'Only task schema type nodes are expected to be mapped from'
        );
        return this.buildSchemaMapperConfigForNodePair(sourceNode, targetNode);
      })
    );
  }

  /**
   * @returns A schema mapper config spec for the node pair, or null if no new schema mapper configs are needed.
   */
  private buildSchemaMapperConfigForNodePair(
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData
  ): ProcessFlowchartSchemaMapperConfigSpec | null {
    const mapsFromType = this.getMapperTokenTypeFromTransitionSource(
      this.getTransitionSourceForNode(sourceNode, targetNode)
    );
    const mapsToType =
      targetNode.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
        ? ProcessMapperTokenType.PROCESS_OUTPUT
        : ProcessMapperTokenType.PROCESS_TASK;

    const schemaMapperKey = this.getSchemaMapperConfigKey(
      sourceNode.taskDefinitionVersionSpec.clientId,
      targetNode.taskDefinitionVersionSpec.clientId,
      mapsFromType,
      mapsToType
    );

    const existingSchemaMapperConfig = this.existingSchemaMapperConfigs.get(schemaMapperKey);

    if (existingSchemaMapperConfig) {
      this.inboundSchemaMapperConfigsByNodeId[targetNode.id].push({
        toNodeConfigClientId: targetNode.id,
        schemaMapperConfigClientId: existingSchemaMapperConfig.clientId,
        schemaMapperDependencies: [{fromNodeConfigClientId: sourceNode.id}],
      });

      return null;
    } else {
      const newSchemaFieldMapperConfigs = this.buildSchemaFieldMapperConfigsNodePair(sourceNode, targetNode);

      const clientId = GetClientId.getClientId();

      this.inboundSchemaMapperConfigsByNodeId[targetNode.id].push({
        toNodeConfigClientId: targetNode.id,
        schemaMapperConfigClientId: clientId,
        schemaMapperDependencies: [{fromNodeConfigClientId: sourceNode.id}],
      });

      const newSchemaMapperConfig: ProcessFlowchartSchemaMapperConfigSpec = {
        clientId,
        toProcessTaskDefinitionVersionClientId: targetNode.taskDefinitionVersionSpec.clientId,
        mapsToType,
        schemaFieldMapperConfigs: newSchemaFieldMapperConfigs.map(({fieldMapperConfig}) => fieldMapperConfig),
        mapperType: ProcessSchemaMapperType.FIELD_MAPPER,
        schemaMapperSourceConfigs: [
          {
            mapsFromType,
            fromProcessTaskDefinitionVersionClientId: sourceNode.taskDefinitionVersionSpec.clientId,
          },
        ],
      };

      this.existingSchemaMapperConfigs.set(schemaMapperKey, newSchemaMapperConfig);

      return newSchemaMapperConfig;
    }
  }

  private getTransitionSourceForNode(
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData
  ): TransitionSource {
    const taskSchemaNodes = this.nodes.filter((node) => isStaticTaskSchemaNodeData(node));

    const transitionSource =
      this.transitionSourceByNodeIdPair[getTransitionSourceByNodeIdPairKey(sourceNode.id, targetNode.id)] ??
      getDefaultTransitionSource(sourceNode.id, taskSchemaNodes);
    return transitionSource;
  }

  /**
   * Gets schema field mapper configs for a pair of nodes.
   */
  private buildSchemaFieldMapperConfigsNodePair(
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData
  ): Array<ProcessFlowchartSchemaFieldMapperConfig> {
    // Find data flow edges that match the same source and target as this control flow edge
    const matchingDataFlowEdges = this.edges.filter(
      (edge) =>
        edge.type === ProcessFlowchartEdgeType.DATA_FLOW &&
        edge.source === sourceNode.id &&
        edge.target === targetNode.id
    );

    const newConfigs: Array<ProcessFlowchartSchemaFieldMapperConfig> = [];

    for (const edge of matchingDataFlowEdges) {
      const schemaFieldMapperConfig = this.buildSchemaFieldMapperConfig(edge, sourceNode, targetNode);
      const config = {
        fieldMapperConfig: schemaFieldMapperConfig,
        fromTaskDefinitionVersionId: sourceNode.taskDefinitionVersionSpec.clientId,
        fromNodeConfigClientId: sourceNode.id,
      };
      newConfigs.push(config);
    }

    return newConfigs;
  }

  private buildSchemaFieldMapperConfig(
    dataFlowEdge: FlowchartStaticEdgeData,
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData
  ): ProcessFlowchartSchemaFieldMapperConfigSpec {
    const fromTaskDefinitionVersionId = sourceNode.taskDefinitionVersionSpec.clientId;
    const fromFieldHandle = dataFlowEdge.sourceHandle;
    const toFieldHandle = dataFlowEdge.targetHandle;

    assert(fromFieldHandle != null, `fromFieldHandle is null for edge ${dataFlowEdge.id}`);
    assert(toFieldHandle != null, `toFieldHandle is null for edge ${dataFlowEdge.id}`);
    const fromFieldClientInfo = getFieldInfoFromDataFlowEdgeHandle(fromFieldHandle);

    const toFieldClientId = getFieldInfoFromDataFlowEdgeHandle(toFieldHandle).fieldClientId;
    const customMappingData = this.customMappingDataByEdgeId[dataFlowEdge.id];

    const transitionSource = this.getTransitionSourceForNode(sourceNode, targetNode);
    const mapsFromType = this.getMapperTokenTypeFromTransitionSource(transitionSource);

    return {
      fromProcessTaskDefinitionVersionClientId: fromTaskDefinitionVersionId,
      mapsFromType,
      toProcessFieldClientIdentifier: toFieldClientId,
      resultMustBeNonNullToMap: customMappingData?.resultMustBeNonNullToMap ?? false,
      lookupConfig:
        customMappingData?.lookupConfig != null
          ? customMappingData.lookupConfig
          : this.getLookupConfigFromFieldAndTransitionSource(
              fromFieldClientInfo.sectionType,
              sourceNode,
              targetNode,
              fromFieldClientInfo.fieldClientId
            ),
    };
  }

  /**
   * If the field mapper config is a duplicate of a previously generated field mapper config, returns the client ID of that field mapper config.
   * Otherwise, returns the new field mapper config.
   */
  buildFieldMapperConfigFromDataFlowEdge(
    clientId: string,
    dataFlowEdge: FlowchartStaticEdgeData,
    targetNode: TaskSchemaStaticNodeData
  ): ProcessFlowchartSchemaFieldMapperConfigSpec {
    const sourceNode = this.nodes.find((node) => node.id === dataFlowEdge.source);
    assert(sourceNode != null, 'Expected to find source node', {edge: dataFlowEdge});
    assert(
      isStaticTaskSchemaNodeData(sourceNode),
      'Only task schema type nodes are expected to be mapped from'
    );
    const fromTaskDefinitionVersionId = sourceNode.taskDefinitionVersionSpec.clientId;

    const fromFieldHandle = dataFlowEdge.sourceHandle;
    const toFieldHandle = dataFlowEdge.targetHandle;

    assert(fromFieldHandle != null, `fromFieldHandle is null for edge ${dataFlowEdge.id}`);
    assert(toFieldHandle != null, `toFieldHandle is null for edge ${dataFlowEdge.id}`);
    const fromFieldClientInfo = getFieldInfoFromDataFlowEdgeHandle(fromFieldHandle);

    const toFieldClientId = getFieldInfoFromDataFlowEdgeHandle(toFieldHandle).fieldClientId;
    const customMappingData = this.customMappingDataByEdgeId[dataFlowEdge.id];

    const transitionSource = this.getTransitionSourceForNode(sourceNode, targetNode);
    const mapsFromType = this.getMapperTokenTypeFromTransitionSource(transitionSource);

    const fieldMapperConfig: ProcessFlowchartSchemaFieldMapperConfigSpec = {
      fromProcessTaskDefinitionVersionClientId: fromTaskDefinitionVersionId,
      mapsFromType,
      toProcessFieldClientIdentifier: toFieldClientId,
      resultMustBeNonNullToMap: customMappingData?.resultMustBeNonNullToMap ?? false,
      lookupConfig:
        customMappingData?.lookupConfig != null
          ? customMappingData.lookupConfig
          : this.getLookupConfigFromFieldAndTransitionSource(
              fromFieldClientInfo.sectionType,
              sourceNode,
              targetNode,
              fromFieldClientInfo.fieldClientId
            ),
    };

    const config = {
      fieldMapperConfig,
      fromTaskDefinitionVersionId,
      fromNodeConfigClientId: sourceNode.id,
    };

    return config.fieldMapperConfig;
  }

  private getLookupConfigFromFieldAndTransitionSource(
    sectionType: TaskSchemaNodeSectionType,
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData,
    fromFieldClientId: string
  ): LookupConfigSpec {
    const lookupSteps: Array<LookupStepSpec> = [];
    if (
      sectionType === TaskSchemaNodeSectionType.INPUTS &&
      this.getTransitionSourceForNode(sourceNode, targetNode) === TransitionSource.OUTPUTS
    ) {
      // If the transition source is the node output but the field is coming from the node input, we need to add
      // a lookup step to go from the output to the inputs (aka from the process output to the process task).
      lookupSteps.push({type: LookupStepType.PROCESS_TASK});
    } else if (sectionType === TaskSchemaNodeSectionType.MAP_FROM_START) {
      // If the field is in the map from start section we first do a flowchart task lookup
      assert(this.rootNode != null, 'Start node must be present to map from root');
      lookupSteps.push({
        type: LookupStepType.FLOWCHART_PROCESS_TASK,
        processTaskSchema: this.rootNode.taskSchemaId,
      });
      lookupSteps.push({
        type: LookupStepType.SCHEMA_FIELD,
        schemaField: fromFieldClientId,
      });
      return {
        isMulti: false,
        lookupSteps,
      };
    }
    lookupSteps.push({
      type: LookupStepType.PROCESS_TASK_DEFINITION_FIELD,
      processTaskDefinitionFieldIdentifier: fromFieldClientId,
      processTaskDefinitionVersion: sourceNode.taskDefinitionVersionSpec.clientId,
    });
    return {
      isMulti: false,
      lookupSteps,
    };
  }

  private getMapperTokenTypeFromTransitionSource(transitionSource: TransitionSource): ProcessMapperTokenType {
    return transitionSource === TransitionSource.OUTPUTS
      ? ProcessMapperTokenType.PROCESS_OUTPUT
      : ProcessMapperTokenType.PROCESS_TASK;
  }
}

/**
 * Builds the default field mapper config for a data flow edge.
 *
 * This is used to differentiate between a "field-to-field" mapping configured by drawing an edge between two
 * fields and a "custom mapping" configured by a custom lookup config.
 */
export function buildDefaultSchemaFieldMapperConfigFromDataFlowEdge({
  clientId,
  dataFlowEdge,
  nodes,
  transitionSourceByNodeIdPair,
}: {
  clientId: string;
  dataFlowEdge: FlowchartStaticEdgeData;
  nodes: Array<StaticFlowchartNodeData>;
  transitionSourceByNodeIdPair: Record<string, TransitionSource>;
}): ProcessFlowchartSchemaFieldMapperConfigSpec {
  const targetNode = nodes.find((node) => node.id === dataFlowEdge.target);
  assert(targetNode != null && isStaticTaskSchemaNodeData(targetNode), 'Expected to find target node', {
    edge: dataFlowEdge,
  });

  const runner = new BuildSchemaMapperConfigsFromStaticDataRunner({
    nodes,
    edges: [dataFlowEdge],
    customMappingDataByEdgeId: {},
    transitionSourceByNodeIdPair,
  });
  return runner.buildFieldMapperConfigFromDataFlowEdge(clientId, dataFlowEdge, targetNode);
}

export default function buildSchemaMapperConfigsFromStaticData(
  args: BuildSchemaMapperConfigsFromStaticDataRunnerArgs
): BuildSchemaMapperConfigsFromStaticDataRunnerResult {
  const runner = new BuildSchemaMapperConfigsFromStaticDataRunner(args);
  return runner.run();
}
